import { JsonPipe, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedModule } from '@src/app/shared/shared.module';

@Component({
  selector: 'app-select-lab',
  templateUrl: './select-lab.component.html',
  styleUrls: ['./select-lab.component.scss'],
  imports: [SharedModule, FormsModule, ReactiveFormsModule, JsonPipe, NgIf],
})
export class SelectLabComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  private logSequence = 0;
  private previousFormValue: Record<string, unknown> | null = null;

  baseOptions: Option[] = [
    { label: 'Sem prioridade', value: 'none' },
    { label: 'Baixa', value: 'low' },
    { label: 'Média', value: 'medium' },
    { label: 'Alta', value: 'high' },
  ];

  delayedOptions: Option[] = [];
  delayedOptionsLoading = true;

  ngModelSingleValue: string | null = null;
  ngModelMultiValues: string[] = [];
  ngModelDelayedValue: string | null = 'medium';

  form: FormGroup;

  logs: string[] = [];
  scenarioLogs: Record<string, string[]> = {
    'empty-initial': [],
    'ngModel-single': [],
    'ngModel-multi': [],
    'ngModel-delayed': [],
    'form-single': [],
    'form-multi': [],
    'form-delayed': [],
    'form-auto': [],
  };

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly fb: FormBuilder
  ) {
    this.form = this.fb.group({
      single: ['low'],
      multi: [['low', 'high']],
      delayedSingle: ['medium'],
      autoSingle: ['medium'],
      emptyInitial: [null],
    });
  }

  ngOnInit(): void {
    this.log('page:init');

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.log('form:valueChanges', value);
      this.logFormControlChanges(value as Record<string, unknown>);
    });

    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(status => {
      this.log('form:statusChanges', { status });
    });

    this.log('scenario:delayed-options:start', {
      initialNgModelDelayedValue: this.ngModelDelayedValue,
      initialFormDelayedValue: this.form.get('delayedSingle')?.value,
    });
    this.logToScenario('ngModel-delayed', 'scenario:start', {
      initialValue: this.ngModelDelayedValue,
    });
    this.logToScenario('form-delayed', 'scenario:start', {
      initialValue: this.form.get('delayedSingle')?.value,
    });

    setTimeout(() => {
      this.delayedOptions = [...this.baseOptions];
      this.delayedOptionsLoading = false;
      this.log('scenario:delayed-options:loaded', { optionsCount: this.delayedOptions.length });
      this.logToScenario('ngModel-delayed', 'scenario:options-loaded', {
        optionsCount: this.delayedOptions.length,
      });
      this.logToScenario('form-delayed', 'scenario:options-loaded', {
        optionsCount: this.delayedOptions.length,
      });
      this.cdr.detectChanges();
    }, 1800);
  }

  clearLogs(): void {
    this.logs = [];
    this.logSequence = 0;
    this.clearScenarioLogs();
    this.log('logs:cleared');
  }

  resetAllStates(): void {
    this.ngModelSingleValue = null;
    this.ngModelMultiValues = [];
    this.ngModelDelayedValue = 'medium';

    this.form.reset({
      single: 'low',
      multi: ['low', 'high'],
      delayedSingle: 'medium',
      autoSingle: 'medium',
      emptyInitial: null,
    });

    this.log('actions:reset-all');
  }

  onNgModelChange(name: string, value: unknown): void {
    this.log(`ngModelChange:${name}`, value);
    this.logToScenario(name, 'ngModelChange', value);
  }

  onOutput(name: string, value?: unknown): void {
    this.log(`output:${name}`, value);
    const scenario = this.extractScenarioName(name);
    this.logToScenario(scenario, `output:${name}`, value);
  }

  getScenarioLogText(scenario: string): string {
    const scenarioLogs = this.scenarioLogs[scenario] ?? [];
    return scenarioLogs.length > 0 ? scenarioLogs.join('\n') : 'Sem eventos neste cenário.';
  }

  private log(event: string, payload?: unknown): void {
    this.logSequence += 1;
    const prefix = `[${this.logSequence}] ${event}`;
    const suffix = payload === undefined ? '' : ` ${JSON.stringify(payload)}`;
    this.logs = [...this.logs, `${prefix}${suffix}`];
  }

  private clearScenarioLogs(): void {
    Object.keys(this.scenarioLogs).forEach(key => {
      this.scenarioLogs[key] = [];
    });
  }

  private extractScenarioName(eventName: string): string {
    const [scenario] = eventName.split('.');
    return scenario;
  }

  private logToScenario(scenario: string, event: string, payload?: unknown): void {
    if (!this.scenarioLogs[scenario]) {
      this.scenarioLogs[scenario] = [];
    }

    this.logSequence += 1;
    const prefix = `[${this.logSequence}] ${event}`;
    const suffix = payload === undefined ? '' : ` ${JSON.stringify(payload)}`;
    this.scenarioLogs[scenario] = [...this.scenarioLogs[scenario], `${prefix}${suffix}`];
  }

  private logFormControlChanges(currentValue: Record<string, unknown>): void {
    const mapping: Record<string, string> = {
      single: 'form-single',
      multi: 'form-multi',
      delayedSingle: 'form-delayed',
      autoSingle: 'form-auto',
    };

    if (!this.previousFormValue) {
      this.previousFormValue = { ...currentValue };
      return;
    }

    Object.entries(mapping).forEach(([controlName, scenario]) => {
      const current = currentValue[controlName];
      const previous = this.previousFormValue?.[controlName];
      if (JSON.stringify(current) !== JSON.stringify(previous)) {
        this.logToScenario(scenario, `formControl:${controlName}.valueChanges`, current);
      }
    });

    this.previousFormValue = { ...currentValue };
  }
}
